import { useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconLockCloseFilled, IconTimesDefault } from '@iwoca/icons';
import { Button, CheckBox, Message, Modal } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useAmplitude } from 'hooks/useAmplitude';
import { useInvalidateAuth } from 'hooks/useAuth';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { getDuplicateEmailHandlers } from '../../duplicateEmail.helpers';
import styles from '../../RegistrationForm.module.css';
import { AccountInformationDE } from './AccountInformationDE/AccountInformationDE';
import { CompanyInformationDE } from './CompanyInformationDE/CompanyInformationDE';
import { OnboardingDocumentsDE } from './OnboardingDocumentsDE/OnboardingDocumentsDE';
import {
  getOnboardingSchemaDE,
  submitOnboardingDE,
} from './onboardingFormDE.helpers';
import { TOnboardingFormValuesDE } from './onboardingFormDE.types';

type TProps = {
  introducerType: 'broker' | 'partner';
  onConfirmingChange: (isConfirming: boolean) => void;
};

export const OnboardingFormDE = ({
  introducerType,
  onConfirmingChange,
}: TProps) => {
  const navigate = useNavigate();
  const invalidateAuth = useInvalidateAuth();
  const { trackEvent } = useAmplitude();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const methods = useForm<TOnboardingFormValuesDE>({
    defaultValues: { introducerType },
    resolver: yupResolver(getOnboardingSchemaDE()),
  });

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = methods;

  const { onDuplicateEmail } = getDuplicateEmailHandlers({
    trigger,
    setError,
    message: t`That email already exists.`,
  });

  // Sync the type the schema validates against; drop the §34c file for partners.
  useEffect(() => {
    setValue('introducerType', introducerType);
    if (introducerType === 'partner') {
      setValue('license34cFile', []);
    }
  }, [introducerType, setValue]);

  const setConfirmingAndLockGate = (value: boolean) => {
    setIsConfirming(value);
    onConfirmingChange(value);
  };

  const onSubmit = async (values: TOnboardingFormValuesDE) => {
    if (!isConfirming) {
      setConfirmingAndLockGate(true);
      return;
    }

    setIsLoading(true);

    const registeredTradingAddress = `${values.addressLine1} ${values.addressLine2}, ${values.town}`;

    await submitOnboardingDE({
      values: {
        ...values,
        introducerType,
        registeredTradingAddress,
      },
      onSuccess: async () => {
        trackEvent(AMPLITUDE_EVENTS.introducerRegisterSubmitted, {
          'Introducer type': introducerType,
          'Invite link': null,
        });

        await invalidateAuth();
        navigate(PATH_NAMES.home());
      },
      onError: () => {
        datadogLogs.logger.error('Failed to submit broker registration', {
          introducerType,
        });
        setShowErrorModal(true);
      },
      onDuplicateEmail: () => {
        setConfirmingAndLockGate(false);
        onDuplicateEmail();
      },
    });

    setIsLoading(false);
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <CompanyInformationDE disabled={isConfirming} />
        <AccountInformationDE disabled={isConfirming} />
        <OnboardingDocumentsDE
          introducerType={introducerType}
          disabled={isConfirming}
        />
        <div className={styles.footerItem}>
          <div className={styles.privacyPolicyAgreement}>
            <CheckBox
              {...register('privacyPolicyAgreement')}
              id="privacyPolicyAgreement"
              name="privacyPolicyAgreement"
              data-testid="privacyPolicyAgreement"
              disabled={isConfirming}
            />
            <div className={styles.privacyPolicyDescription}>
              <p>
                <Trans>
                  I have read and agree to the{' '}
                  <a
                    href="https://static.iwoca.com/pdf/de/iwoca_Partner_AGB_v20240816.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    iwoca Partner terms and conditions
                  </a>
                  .
                </Trans>
              </p>
              {errors.privacyPolicyAgreement && (
                <Message
                  message={errors.privacyPolicyAgreement.message || ''}
                  type="error"
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant={isConfirming ? 'success' : 'primary'}
            type="submit"
            loading={isLoading}
          >
            {isConfirming ? t`Confirm` : t`Sign up`}
            <IconLockCloseFilled />
          </Button>
          {isConfirming && (
            <Button
              className={styles.cancelButton}
              variant="tertiary"
              type="button"
              onClick={() => setConfirmingAndLockGate(false)}
            >
              <Trans>Cancel</Trans>
              <IconTimesDefault />
            </Button>
          )}
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            <Trans>Here's to a successful partnership!</Trans>
          </p>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t`There's a problem with the info you've entered.`}
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          <Trans>
            If you have an account with iwoca, use the login button in the top
            right. Otherwise, give us a call and we'll help you get registered.
          </Trans>
        </p>
      </Modal>
    </FormProvider>
  );
};
